import { defineStore } from 'pinia';

import type { InventoryItem } from '@stuut/api/models';

import { stuut } from '@/api';

interface State {
  _setupPromise: Promise<InventoryItem[] | undefined> | null;
  initLoad: boolean;
  inventoryItems: InventoryItem[];
}

export const useInventoryItems = defineStore('inventoryItems', {
  actions: {
    async fetchItems(args?) {
      const inventoryItems = await stuut.inventoryItems.list({
        params: args || {},
      });

      this.$patch({ inventoryItems });

      return this.inventoryItems;
    },

    async peekFindItem(id) {
      const record = this.getById(id);

      if (record) return record;

      const inventoryItem = await stuut.inventoryItems.retrieve(id);

      return inventoryItem;
    },

    // Multiple components may call setup() concurrently (e.g. the cell calls it
    // fire-and-forget on mount, then the dropdown loader awaits it on open).
    // Store the in-flight promise so concurrent callers await the same fetch
    // instead of returning early with an empty store.
    async setup() {
      if (this.inventoryItems?.length) return;

      if (this._setupPromise) return this._setupPromise;

      this.initLoad = true;
      this._setupPromise = this.fetchItems();

      const inventoryItems = await this._setupPromise;
      this._setupPromise = null;

      return inventoryItems;
    },
  },

  getters: {
    getById: (state) => {
      return (id: string) => state.inventoryItems.find((item) => item?.id === id);
    },
  },

  state: (): State => ({
    _setupPromise: null,
    initLoad: false,
    inventoryItems: [],
  }),
});

export default useInventoryItems;
